import React, { useState, useRef, useEffect } from 'react'
import { Dropdown } from 'rsuite'
import { ReactComponent as DownArrow } from '../../../assets/svgs/down-arrow.svg'
import { ReactComponent as UpArrow } from '../../../assets/svgs/up-arrow.svg'
import { t } from '../../../utils/i18n'
import './dropdown.scss'
import { CanBeAnything } from '../../../types'
import { truncateString } from '../../../containers/utils'

export interface DropdownOption {
  name: string
  id: string
  value?: CanBeAnything
  label?: CanBeAnything
}

interface CustomDropdownProps {
  id: string
  label: string
  placeholder?: string
  options: DropdownOption[]
  onSelect: (option: DropdownOption) => void
  className?: string
  defaultSelectedId?: string
  isDisabled?: boolean
}

export const CustomDropdown: React.FC<CustomDropdownProps> = ({
  label,
  id,
  placeholder = t('dropdown.placeholder'),
  options,
  onSelect,
  className = '',
  defaultSelectedId,
  isDisabled = false,
}) => {
  const [selected, setSelected] = useState<string | number | null>(null)
  const [open, setOpen] = useState(false)
  const toggleRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (defaultSelectedId) {
      const defaultOption = options.find((opt) => opt.id === defaultSelectedId)
      if (defaultOption) {
        setSelected(truncateString(defaultOption.name, 25))
      }
    }
  }, [defaultSelectedId, options, onSelect])

  const handleToggle = (isOpen: boolean) => {
    if (isOpen) {
      setOpen(false)
    } else {
      setOpen(isOpen)
    }
  }

  const handleSelect = (value: string | number) => {
    const selectedOption = options.find((opt) => opt.id === value)
    if (selectedOption) {
      setSelected(truncateString(selectedOption.name, 25))
      onSelect(selectedOption)
      setOpen(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      console.log("enter | '' ")
      e.preventDefault()
      setOpen(true)
    } else if (e.key === 'Escape') {
      setOpen(false)
    }
  }

  return (
    <div className={`custom-dropdown-wrapper ${className}`}>
      <label className="label-input dropdown-label" id={`${id}-label`} htmlFor={`${id}-toggle`}>
        {label}
      </label>

      <Dropdown
        open={open}
        onToggle={() => handleToggle(open)}
        onSelect={(v) => handleSelect(v)}
        className={`custom-dropdown ${isDisabled ? 'disabled' : ''}`}
        renderToggle={(props, ref) => (
          <div
            ref={(el) => {
              toggleRef.current = el
              if (typeof ref === 'function') ref(el)
              else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = el
            }}
            {...props}
            className="dropdown-toggle"
            id={`${id}-toggle`}
            role="combobox"
            aria-autocomplete="none"
            tabIndex={0}
            aria-expanded={open}
            aria-controls={`${id}-list`}
            aria-labelledby={`${id}-label`}
            onClick={() => setOpen((prev) => !prev)}
            onKeyDown={handleKeyDown}
            onBlur={(e) => {
              // Close only if focus goes outside the dropdown
              if (!e.currentTarget.contains(e.relatedTarget)) {
                setOpen(true)
              }
            }}
          >
            <div className={`dropdown-label-wrapper ${isDisabled ? 'disabled' : ''}`}>
              <span className="placeholder-text form-label">
                {selected || placeholder || 'Select'}
              </span>
              {!open ? (
                <DownArrow className="dropdown-arrow" />
              ) : (
                <UpArrow className="dropdown-arrow" />
              )}
            </div>
          </div>
        )}
      >
        <div role="listbox" id={`${id || 'custom-dropdown'}-list`}>
          {options?.map((opt, i) => (
            <Dropdown.Item
              key={i}
              eventKey={opt.id}
              role="option"
              aria-selected={opt.name === selected}
              className={`label-input ${opt.name === selected ? 'selected-item form-label' : ''}`}
            >
              {opt.name}
            </Dropdown.Item>
          ))}
        </div>
      </Dropdown>
    </div>
  )
}

export default CustomDropdown
